import * as BABYLON from '@babylonjs/core'
import { DisposerBag } from './disposerBag'

export interface WalkCameraOptions {
  name?: string
  start: BABYLON.Vector3
  lookAt: BABYLON.Vector3
  eyeHeight: number
  bounds: { x: number; z: number }
  fov?: number
  speed?: number
  angularSensibility?: number
  inertia?: number
  /** Extra per-frame work (e.g. animating a texture offset) — runs after the bounds clamp each frame. */
  onBeforeRender?: () => void
}

export interface WalkCameraResult {
  camera: BABYLON.UniversalCamera
}

/**
 * First-person `UniversalCamera` clamped to a rectangular floor footprint —
 * WASD/arrows via Babylon's own key bindings, eye height locked every
 * frame, no wheel-zoom bound (so there's no way to zoom "through" a wall).
 * Generalizes Mountain Lot's camera + its bounds-clamp render loop.
 */
export function buildWalkCamera(scene: BABYLON.Scene, canvas: HTMLCanvasElement, bag: DisposerBag, opts: WalkCameraOptions): WalkCameraResult {
  const cam = bag.track(new BABYLON.UniversalCamera(opts.name ?? 'walkkit-cam', opts.start.clone(), scene))
  cam.minZ = 0.05
  cam.fov = opts.fov ?? 0.9
  cam.speed = opts.speed ?? 0.13
  cam.angularSensibility = opts.angularSensibility ?? 3200
  cam.inertia = opts.inertia ?? 0.75
  cam.keysUp = [87, 38]
  cam.keysDown = [83, 40]
  cam.keysLeft = [65, 37]
  cam.keysRight = [68, 39]
  cam.setTarget(opts.lookAt)
  cam.attachControl(canvas, true)
  scene.activeCamera = cam

  const { x: boundX, z: boundZ } = opts.bounds
  const observer = scene.onBeforeRenderObservable.add(() => {
    cam.position.x = Math.max(-boundX, Math.min(boundX, cam.position.x))
    cam.position.z = Math.max(-boundZ, Math.min(boundZ, cam.position.z))
    cam.position.y = opts.eyeHeight
    opts.onBeforeRender?.()
  })
  bag.defer(() => scene.onBeforeRenderObservable.remove(observer))

  return { camera: cam }
}
